import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LogOut, ShieldCheck, Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const NAV = [
  { to: "/", label: "Willkommen", end: true },
  { to: "/galerie", label: "Galerie" },
  { to: "/uebernachtung", label: "Übernachtung" },
  { to: "/anfahrt", label: "Anfahrt" },
  { to: "/mitglieder", label: "Mitglieder" },
  { to: "/turniere", label: "Turniere" },
  { to: "/abrechnung", label: "Abrechnung 2026" },
  { to: "/ausflugsziele", label: "Ausflugsziele" },
  { to: "/kommunikation", label: "Pinnwand" },
  { to: "/gaestebuch", label: "Gästebuch" },
  { to: "/anleitung", label: "Anleitung" },
];

export default function Layout() {
  const { logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [footerText, setFooterText] = useState("Familientreffen · a-ebel.com");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/branding").then((r) => r.data.footer_text && setFooterText(r.data.footer_text)).catch(() => {});
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <div className="min-h-screen paper-grain text-[#1A1C19] flex flex-col">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <NavLink to="/" className="flex items-baseline gap-2" data-testid="brand-link">
            <span className="font-serif text-2xl tracking-tight">Familie Ebel</span>
            <span className="label-eyebrow hidden sm:inline">Treffen 2026</span>
          </NavLink>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `link-underline text-sm tracking-wide ${isActive ? "active text-[#48634A]" : "text-[#4A4F46] hover:text-[#1A1C19]"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {isAdmin && (
              <button
                onClick={() => navigate("/admin")}
                className="hidden sm:inline-flex items-center gap-1.5 text-xs tracking-wide text-[#48634A] hover:text-[#364A38]"
                data-testid="admin-link"
              >
                <ShieldCheck className="w-4 h-4" /> Admin
              </button>
            )}
            <button
              onClick={() => {
                logout();
                navigate("/login");
              }}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs tracking-wide text-[#4A4F46] hover:text-[#1A1C19]"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" /> Abmelden
            </button>

            {/* Hamburger button (visible < lg) */}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#E5DFD3] bg-white text-[#1A1C19] hover:bg-[#F4EFE6]"
              aria-label={menuOpen ? "Menü schließen" : "Menü öffnen"}
              aria-expanded={menuOpen}
              data-testid="hamburger-btn"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile slide-in menu */}
      {menuOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/30 z-40"
            onClick={() => setMenuOpen(false)}
            data-testid="mobile-menu-overlay"
          />
          <aside
            className="lg:hidden fixed top-0 right-0 bottom-0 w-[82%] max-w-sm bg-[#FBF7EF] z-50 shadow-2xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-16 px-6 flex items-center justify-between border-b border-[#E5DFD3]">
              <span className="font-serif text-xl tracking-tight">Menü</span>
              <button
                onClick={() => setMenuOpen(false)}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-[#F4EFE6]"
                aria-label="Menü schließen"
                data-testid="mobile-menu-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-2 py-4">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`mnav-${item.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-xl text-base ${
                      isActive
                        ? "bg-[#48634A] text-white"
                        : "text-[#1A1C19] hover:bg-[#F4EFE6]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-[#E5DFD3] p-4 space-y-2">
              {isAdmin && (
                <button
                  onClick={() => navigate("/admin")}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-[#E5DFD3] text-[#48634A] hover:bg-[#F4EFE6]"
                  data-testid="mobile-admin-link"
                >
                  <ShieldCheck className="w-4 h-4" /> Admin-Bereich
                </button>
              )}
              <button
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-[#E5DFD3] text-[#4A4F46] hover:bg-[#F4EFE6]"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-4 h-4" /> Abmelden
              </button>
            </div>
          </aside>
        </>
      )}

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-24 border-t border-[#E5DFD3] py-10 text-center">
        <div className="divider-ornament max-w-md mx-auto mb-4">
          <span className="font-serif italic text-lg">Ebel</span>
        </div>
        <p className="text-xs tracking-[0.24em] uppercase text-[#4A4F46]" data-testid="footer-text">
          {footerText}
        </p>
      </footer>
    </div>
  );
}
